import { emptySettings } from "../landingEditor/config";
import { defaultLandingLayout, getLandingSegments } from "../landingEditor/landingLayout";

const GENERIC = Object.freeze({
  heroSecondaryLabel: "Ver productos",
  heroSecondaryUrl: "/productos",
  productsLabel: "Productos",
  productsUrl: "/productos",
  featured: Object.freeze({
    title: "Productos destacados",
    subtitle: "Conocé las últimas novedades del catálogo.",
    loadingText: "Cargando productos…",
    emptyText: "Todavía no hay productos para mostrar.",
    errorText: "No se pudieron cargar los productos.",
  }),
  categories: Object.freeze({
    title: "Explorá por categorías",
    loadingText: "Cargando categorías…",
    emptyText: "Todavía no hay categorías para mostrar.",
    errorText: "No se pudieron cargar las categorías.",
    ctaLabel: "Ver productos",
    productSingular: "producto",
    productPlural: "productos",
  }),
  about: Object.freeze({
    eyebrow: "Sobre nuestro negocio",
    title: "Conocé nuestro negocio",
    description: "Trabajamos para ofrecer una atención cercana, soluciones claras y una experiencia confiable en cada consulta.",
  }),
  contactTitle: "Contacto",
});

const GASTRONOMIC = Object.freeze({
  heroSecondaryLabel: "Ver carta",
  heroSecondaryUrl: "/carta",
  featured: Object.freeze({
    title: "Destacados de la carta",
    subtitle: "Conocé los destacados de nuestra carta.",
    loadingText: "Cargando destacados…",
    emptyText: "Todavía no hay destacados para mostrar en la carta.",
    errorText: "No se pudieron cargar los destacados de la carta.",
  }),
  categories: Object.freeze({
    title: "Explorá nuestra carta",
    loadingText: "Cargando secciones de la carta…",
    emptyText: "Todavía no hay secciones de la carta para mostrar.",
    errorText: "No se pudieron cargar las secciones de la carta.",
    ctaLabel: "Ver en la carta",
    productSingular: "opción",
    productPlural: "opciones",
  }),
  about: Object.freeze({
    eyebrow: "Nuestra propuesta",
    title: "Conocé nuestra propuesta",
    description: "Conocé más sobre el negocio y la propuesta que compartimos en nuestra carta.",
  }),
  contactTitle: "Ubicación, horarios y contacto",
});

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function sameText(value, recognized) {
  const normalized = String(value ?? "");
  return normalized === "" || recognized.includes(normalized);
}

function setIfRecognized(next, path, recognized, replacement, changes, allowRecognizedDefaults = true) {
  const parts = path.split(".");
  let cursor = next;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const key = parts[index];
    if (!cursor[key] || typeof cursor[key] !== "object") cursor[key] = {};
    cursor = cursor[key];
  }
  const key = parts[parts.length - 1];
  const current = String(cursor[key] ?? "");
  const adaptable = current === "" || (allowRecognizedDefaults && recognized.includes(current));
  if (!adaptable) return false;
  if (cursor[key] === replacement) return false;
  cursor[key] = replacement;
  changes.push(path);
  return true;
}

function isDefaultLayout(form) {
  const current = getLandingSegments(form);
  const defaults = defaultLandingLayout().segments;
  if (JSON.stringify(form?.sections || {}) !== JSON.stringify(emptySettings.sections || {})) return false;
  if (current.length !== defaults.length) return false;
  return current.every((segment, index) => {
    const expected = defaults[index];
    return segment.id === expected.id
      && segment.type === expected.type
      && segment.visible !== false
      && !segment.anchor
      && !segment.data
      && (!segment.visualStyle || Object.keys(segment.visualStyle).length === 0);
  });
}

function isDefaultHighlights(form) {
  return JSON.stringify(form?.highlightsIntro || {}) === JSON.stringify(emptySettings.highlightsIntro)
    && JSON.stringify(form?.highlights || []) === JSON.stringify(emptySettings.highlights);
}

function isDefaultBrands(form) {
  const current = form?.catalog?.brands || {};
  const baseline = emptySettings.catalog.brands;
  return JSON.stringify(current) === JSON.stringify(baseline);
}

function isDefaultSpotlight(form) {
  const current = form?.catalog?.spotlight || {};
  const baseline = emptySettings.catalog.spotlight;
  return JSON.stringify(current) === JSON.stringify(baseline);
}

export function inspectGastronomicLandingPreset(form = {}) {
  const next = clone(form);
  const changes = [];
  const groups = new Set();
  const structureDefault = isDefaultLayout(form);

  const touch = (group, path, recognized, replacement) => {
    if (setIfRecognized(next, path, recognized, replacement, changes, structureDefault)) groups.add(group);
  };

  const currentHomeSource = String(next.catalog?.homeSource ?? "").trim();
  const homeSourceIsEmpty = !currentHomeSource;
  const homeSourceIsInitialBaseline = currentHomeSource === emptySettings.catalog.homeSource;
  if (
    currentHomeSource !== "gastronomicMenu"
    && (homeSourceIsEmpty || (structureDefault && homeSourceIsInitialBaseline))
  ) {
    next.catalog = { ...(next.catalog || {}), homeSource: "gastronomicMenu" };
    changes.push("catalog.homeSource");
    groups.add("content");
  }

  // `showProductsLink` is the canonical catalog-access visibility switch in both
  // editor and storefront. Never turn it off to deduplicate Carta/Productos:
  // semantic resolution converts the default catalog access into Carta.

  const heroSecondaryLabel = String(next.hero?.secondaryCtaLabel ?? "");
  const heroSecondaryUrl = String(next.hero?.secondaryCtaUrl ?? "");
  const heroSecondaryEmpty = !heroSecondaryLabel && !heroSecondaryUrl;
  const heroSecondaryInitialDefault = structureDefault
    && sameText(heroSecondaryLabel, [GENERIC.heroSecondaryLabel])
    && sameText(heroSecondaryUrl, [GENERIC.heroSecondaryUrl]);
  if (heroSecondaryEmpty || heroSecondaryInitialDefault) {
    if (next.hero.secondaryCtaLabel !== GASTRONOMIC.heroSecondaryLabel) {
      next.hero.secondaryCtaLabel = GASTRONOMIC.heroSecondaryLabel;
      changes.push("hero.secondaryCtaLabel");
    }
    if (next.hero.secondaryCtaUrl !== GASTRONOMIC.heroSecondaryUrl) {
      next.hero.secondaryCtaUrl = GASTRONOMIC.heroSecondaryUrl;
      changes.push("hero.secondaryCtaUrl");
    }
    groups.add("access");
  }

  Object.entries(GASTRONOMIC.featured).forEach(([key, value]) => {
    touch("text", `catalog.featured.${key}`, [GENERIC.featured[key]], value);
  });
  Object.entries(GASTRONOMIC.categories).forEach(([key, value]) => {
    touch("text", `catalog.categories.${key}`, [GENERIC.categories[key]], value);
  });
  Object.entries(GASTRONOMIC.about).forEach(([key, value]) => {
    const recognized = [GENERIC.about[key]];
    const businessName = String(form?.business?.name || "").trim();
    if (key === "title" && businessName) recognized.push(`Conocé ${businessName}`);
    touch("text", `about.${key}`, recognized, value);
  });
  touch("text", "contact.title", [GENERIC.contactTitle], GASTRONOMIC.contactTitle);

  if (structureDefault) {
    const layout = getLandingSegments(next);
    const byType = Object.fromEntries(layout.map((segment) => [segment.type, segment]));
    const order = ["navbar", "hero", "featured", "categories", "about", "highlights", "contact", "spotlight", "brands", "footer"];
    next.layout = { segments: order.map((type) => clone(byType[type])).filter(Boolean) };
    changes.push("layout.segments");
    groups.add("structure");

    next.sections = { ...(next.sections || {}) };
    if (isDefaultSpotlight(form) && next.sections.showSpotlight !== false) {
      next.sections.showSpotlight = false;
      changes.push("sections.showSpotlight");
    }
    if (isDefaultBrands(form) && next.sections.showBrands !== false) {
      next.sections.showBrands = false;
      changes.push("sections.showBrands");
    }
    if (isDefaultHighlights(form) && next.sections.showHighlights !== false) {
      next.sections.showHighlights = false;
      changes.push("sections.showHighlights");
    }
  }

  return {
    nextForm: next,
    changed: changes.length > 0,
    changes,
    groups: [...groups],
    structureDefault,
  };
}
